/**
 * Tag Scoping Utilities
 *
 * Defines who can be assigned which tags based on the target user's role and
 * workspace. Mirrors the server-side scope rules so we can:
 *   1. Configure a server-side TagAutocomplete query that returns only valid tags
 *   2. Locally re-validate already-selected tag chips when the target's role
 *      or workspace changes (so stale picks get cleaned up).
 *
 * Business rules:
 * - Admins can be assigned ALL tags
 * - Account-level users (no workspace) → only account-wide tags (workspaceId IS NULL)
 * - Workspace users → account-wide tags OR tags from their own workspace
 */

import { Tag, UserRole } from '../apis/api-client';

export interface TagScope {
  workspaceId?: number;
  includeAccountWide?: boolean;
  accountWideOnly?: boolean;
}

/**
 * Build TagAutocomplete scope props for a target user. Caller spreads the result
 * onto the component: <TagAutocomplete {...tagScopeForUser(role, wsId)} />.
 */
export function tagScopeForUser(
  targetUserRole: UserRole | undefined,
  targetUserWorkspaceId: number | null | undefined
): TagScope {
  if (targetUserRole === UserRole.Admin) return {};
  if (!targetUserWorkspaceId) return { accountWideOnly: true };
  return { workspaceId: targetUserWorkspaceId, includeAccountWide: true };
}

/**
 * Local predicate matching the server's scope check — used to re-validate
 * already-selected tags after a role/workspace change.
 */
export function isTagValidForUser(
  tag: Tag,
  targetUserRole: UserRole | undefined,
  targetUserWorkspaceId: number | null | undefined
): boolean {
  if (targetUserRole === UserRole.Admin) return true;
  if (!targetUserWorkspaceId) return !tag.workspaceId;
  return !tag.workspaceId || tag.workspaceId === targetUserWorkspaceId;
}

/**
 * Tags are admin-only metadata used for workspace content scoping —
 * AccountAdmin/Admin only.
 */
export function canEditUserTags(currentUserRole: UserRole | undefined): boolean {
  return currentUserRole === UserRole.AccountAdmin || currentUserRole === UserRole.Admin;
}
